import type { Condition } from "building-types/conditions/conditionTypes";
import type { Doc } from "building-types/doc";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type { PropWithLevelId } from "building-types/props/propTypes";
import type { RuleBasedSelector } from "building-types/selectors/ruleBasedSelectorTypes";
import { EffectType, type Effect } from "building-types/views/effectTypes";
import type { SheetGeometryCategoryStyle, ViewTemplate } from "building-types/views/planSets";
import type { ViewTemplateOverride } from "building-types/views/planSets";
import {
  type CompiledSelectorFormulasByEntityType,
  type SerializableSheetGeometrySelector,
  RuleBasedSelectorBuildingEntityTypes,
  SelectionMethod,
} from "building-types/views/SerializableSheetGeometrySelector";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import {
  isSheetGeometryLineBased,
  isSheetGeometryTextBased,
  SheetGeometryType,
} from "building-types/views/sheetGeometryTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { numMul } from "common/utils/nominalNumbers";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import type { Mutable } from "common/utils/types";
import { docGetRuleBasedSelectorsFromHandle } from "doc/docGetRuleBasedSelectors";
import { docGetSortedViewTemplateOverrides } from "doc/docGetViewTemplateOverrides";
import docEntityGetChildren from "doc/entity/docEntityGetChildren";
import { compileFormula } from "doc/formula/formulaConditions";
import { formulaNew } from "doc/formula/formulaNew";
import type { SheetGeometryFilter } from "doc/plans/planGeometrySource";
import { filterForGeometryGroupCondition, SheetGeometrySourceFilter } from "doc/plans/planGeometrySource";
import type {
  Categorized3DGeometry,
  RelaxationObstacle,
  SheetGeometrySelector,
  SheetGeometryStyler,
  TaggedPlanGeometry,
} from "doc/plans/planGeometrySourceTypes";
import { resolveSheetGeometryStyle, tagSelector } from "doc/plans/primitives/primitiveStyles";
import type { TagStyleSheet } from "doc/plans/primitives/primitiveStylesTypes";
import {
  isOverridableSheetGeometryCategory,
  isScaleableSheetGeometryCategory,
  OverridableSheetGeometryCategories,
  OverridableSheetGeometryGroupCategories,
} from "doc/plans/sheetGeometryOverridableCategories";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import type { SheetGeometryCategoryStyleSheets } from "doc/plans/SheetGeometrySourceStyleOverrideTable";
import {
  chunkSelectorRulesByLogicalAnd,
  evalSheetGeometrySelector,
  rulesChunkIsValid,
} from "doc/ruleBasedSelectorTools";
import { collectEntityTypeDefs } from "doc/utils/entityFieldTools";
import {
  effectContainsCategory,
  isRelaxationObstacleEffect,
  isStylingEffect,
  isVisibilityEffect,
} from "doc/views/effectTools";
import { faceCategoryToSheetGeometryCategory } from "../viewTemplateTools";
import { styleSheetCascade } from "./planStyles";
import { planDefaultCategoryStyles } from "./planStyleSheets";

export function makeSortedSelectorEffectMap(
  viewportOverrideIds: string[],
  viewTemplateOverrideIds: string[],
  doc: Doc,
): SheetGeometrySelectorEffectMap[] {
  const sheetGeometrySelectors: SheetGeometrySelectorEffectMap[] = [];

  const viewTemplateOverrides: ViewTemplateOverride[] = [
    ...docGetSortedViewTemplateOverrides(doc, viewTemplateOverrideIds),
    ...docGetSortedViewTemplateOverrides(doc, viewportOverrideIds),
  ];

  // maps a selector id to a set of compiled formulas for each selectable entity type
  const compiledFormulasCache: { [selectorId: string]: CompiledSelectorFormulasByEntityType } = {};

  const typeDefs = collectEntityTypeDefs(doc.labels);

  function compileFormulasForSelector(selectorId: string, selector: RuleBasedSelector) {
    if (compiledFormulasCache[selectorId]) {
      return compiledFormulasCache[selectorId];
    }

    // any rule with a blank typeDefId is invalid
    const rules = selector.rules.filter((rule) => rule.typeDefId);

    if (rules.length === 0) {
      return {};
    }

    // Since there is no mechanism to skip over a rule once it has been compiled into a formula,
    // we need to compile a separate formula for each entity type to skip invalid rules for said entity type.
    // Additionally, a selector's rules need to be validated in chunks to ensure that all rules connected by
    // a logical AND are valid for the entity type.
    const chunkedRules = chunkSelectorRulesByLogicalAnd(rules);

    const compiledSelectorFormulas: CompiledSelectorFormulasByEntityType = {};

    for (const entityType of RuleBasedSelectorBuildingEntityTypes) {
      const validRules: Condition[] = [];

      for (const chunk of chunkedRules) {
        if (rulesChunkIsValid(chunk, entityType)) {
          validRules.push(...chunk);
        }
      }

      if (validRules.length === 0) {
        continue;
      }

      if (validRules[0].connective) {
        validRules[0] = { ...validRules[0], connective: undefined };
      }

      compiledSelectorFormulas[entityType] = formulaNew(
        compileFormula({ conditions: validRules, conditionGroups: {}, typeDefs }),
      );
    }
    compiledFormulasCache[selectorId] = compiledSelectorFormulas;
    return compiledSelectorFormulas;
  }

  // make sheet geometry selectors
  for (const vto of viewTemplateOverrides) {
    const selectionMethod = vto.selectionMethod;
    const vtoSelectors = docGetRuleBasedSelectorsFromHandle(doc, { type: "VIEW_TEMPLATE_OVERRIDE", id: vto.id });
    const formulas: CompiledSelectorFormulasByEntityType[] = Object.keys(vtoSelectors).map((id) =>
      compileFormulasForSelector(id, vtoSelectors[id]),
    );
    let selector: SerializableSheetGeometrySelector = { selectionMethod: SelectionMethod.ALL };

    switch (selectionMethod) {
      case SelectionMethod.ALL:
        selector = { selectionMethod: SelectionMethod.ALL };
        break;
      case SelectionMethod.BY_LABEL: {
        selector = { selectionMethod: SelectionMethod.BY_LABEL, labels: vto.labels };
        break;
      }
      case SelectionMethod.BY_SELECTOR: {
        selector = { selectionMethod: SelectionMethod.BY_SELECTOR, formulas, docLabels: doc.labels };
        break;
      }
      default:
        assertUnreachable(selectionMethod);
    }

    // collect and sort effects for this vto
    const effects = sortEntitiesBySortIndex(
      docEntityGetChildren(doc, { type: "VIEW_TEMPLATE_OVERRIDE", id: vto.id })
        .filter((handle) => handle.type === "EFFECT")
        .map((handle) => doc.effects[handle.id]),
    );

    sheetGeometrySelectors.push({ selector, effects });
  }

  return sheetGeometrySelectors;
}

export function filter3DProp({
  prop,
  selectors,
}: {
  prop: PropWithLevelId;
  selectors?: SheetGeometrySelectorEffectMap[];
}) {
  const sheetGeometryCategory = faceCategoryToSheetGeometryCategory(prop.faceCategory);
  if (!sheetGeometryCategory) {
    return true;
  }
  if (!selectors) {
    return true;
  }
  return evaluateVisibilityEffects(
    {
      buildingEntity: prop,
      category: sheetGeometryCategory,
    },
    selectors,
  );
}

/**
 * @returns true if the geometry should be visible, false otherwise.
 */
function evaluateVisibilityEffects(
  g: TaggedPlanGeometry | Categorized3DGeometry,
  selectors: SheetGeometrySelectorEffectMap[],
): boolean {
  if (!g.buildingEntity) {
    return true;
  }

  for (const { selector, effects } of selectors) {
    const res = evalSheetGeometrySelector(g, selector);

    for (const effect of effects) {
      if (!isVisibilityEffect(effect)) {
        continue;
      }

      if (!effectContainsCategory(effect, g.category)) {
        continue;
      }

      if ((res && !effect.invert) || (!res && effect.invert)) {
        return false;
      }
    }
  }

  return true;
}

export function makeEffectTagForTextStyle(effectId: string): string {
  return `${effectId}_text`;
}

export function makeEffectTagForLineStyle(effectId: string): string {
  return `${effectId}_line`;
}

// Inverse of makeEffectTagForSheetGeometryType. Text and line geometry carry a suffixed tag, so a
// bare `effects[tag]` lookup only resolves for the untagged (SVG/asset) case.
function effectFromTag(tag: string, effects: Table<Effect>): Effect | undefined {
  const direct = effects[tag];
  if (direct) {
    return direct;
  }

  for (const suffix of ["_text", "_line"]) {
    if (tag.endsWith(suffix)) {
      return effects[tag.slice(0, -suffix.length)];
    }
  }

  return undefined;
}

function makeEffectTagForSheetGeometryType(geometry: SheetGeometry, effectId: string): string {
  if (isSheetGeometryTextBased(geometry)) {
    return makeEffectTagForTextStyle(effectId);
  }

  if (isSheetGeometryLineBased(geometry)) {
    return makeEffectTagForLineStyle(effectId);
  }

  return effectId;
}

/**
 * Tags geometries with the styling and scaling effect ids that apply to them.
 */
function evaluateStyleAndScaleEffects(
  g: TaggedPlanGeometry,
  selectorEffectMap: SheetGeometrySelectorEffectMap[],
): TaggedPlanGeometry {
  if (!isOverridableSheetGeometryCategory(g.category) && !isScaleableSheetGeometryCategory(g.category)) {
    return g;
  }

  for (const { selector, effects } of selectorEffectMap) {
    const res = evalSheetGeometrySelector(g, selector);
    if (!res) {
      continue;
    }

    for (const effect of effects) {
      if (isVisibilityEffect(effect)) {
        continue;
      }

      if (isStylingEffect(effect) && !g.buildingEntity) {
        continue;
      }

      if (!effectContainsCategory(effect, g.category)) {
        continue;
      }

      g = { ...g, tags: [...(g.tags ?? []), makeEffectTagForSheetGeometryType(g, effect.id)] };
    }
  }

  return g;
}

function resolveSheetGeometryScale(g: TaggedPlanGeometry, effects: Table<Effect>): TaggedPlanGeometry {
  if (g.type !== SheetGeometryType.SVG && g.type !== SheetGeometryType.TEXT) {
    return g;
  }

  // Every Overridable category already carries SCALING tags (ROOM_LABELS, ANNOT_NOTE, PROP_TAG...),
  // harmless while this only handled SVG. Scaling text means only pickable categories may respond.
  if (g.type === SheetGeometryType.TEXT && !isScaleableSheetGeometryCategory(g.category)) {
    return g;
  }

  let newScale: number | undefined;

  for (const tag of g.tags ?? []) {
    const effect = effectFromTag(tag, effects);
    if (effect?.type === EffectType.SCALING) {
      newScale = effect.scale;
    }
  }

  if (newScale === undefined) {
    return g;
  }

  if (g.type === SheetGeometryType.SVG) {
    return { ...g, scale: newScale };
  }

  // makePlanGeometryList re-styles relaxed geometry, so this runs twice for ELECTRIC_LOAD_LABEL. SVG
  // survives because `scale` is an assignment; text divides out what it already applied.
  const appliedScaleEffect = g.appliedScaleEffect ?? 1;
  if (newScale === appliedScaleEffect) {
    return g;
  }
  const delta = newScale / appliedScaleEffect;

  return {
    ...g,
    fontSize: g.fontSize ? numMul(g.fontSize, delta) : undefined,
    boxWidth: g.boxWidth ? numMul(g.boxWidth, delta) : undefined,
    boxHeight: g.boxHeight ? numMul(g.boxHeight, delta) : undefined,
    offsetX: g.offsetX ? numMul(g.offsetX, delta) : undefined,
    offsetY: g.offsetY ? numMul(g.offsetY, delta) : undefined,
    appliedScaleEffect: newScale,
  };
}

/**
 * Generates style sheets for doc effects. Currently this function returns separate style sheets
 * for line and text styles to prevent clashes between identically named styles.
 */
function makeStyleEffectsStyleSheets(doc: Pick<Doc, "effects">): TagStyleSheet {
  const styleSheet: Mutable<TagStyleSheet> = {};

  for (const effectId in doc.effects) {
    const effect = doc.effects[effectId];
    if (effect.type === EffectType.STYLING) {
      const { lineStyleOverrides, textStyleOverrides } = effect;

      styleSheet[tagSelector(makeEffectTagForLineStyle(effectId))] = {
        ...lineStyleOverrides,
      };

      styleSheet[tagSelector(makeEffectTagForTextStyle(effectId))] = {
        ...textStyleOverrides,
      };
    }
  }

  return styleSheet;
}

/**
 * Generates a source filter from various inputs.
 */
export function makeSheetGeometrySourceFilter({
  selectorEffectMap = [],
  builtInSelectors = {},
  builtInFilters = {},
  provenanceSelectors = {},
  reportingUnitSelectors = {},
}: {
  selectorEffectMap?: SheetGeometrySelectorEffectMap[];
  builtInSelectors?: SheetGeometryCategoryTable<SheetGeometrySelector>;
  builtInFilters?: SheetGeometryCategoryTable<SheetGeometryFilter>;
  provenanceSelectors?: SheetGeometryCategoryTable<SheetGeometrySelector>;
  reportingUnitSelectors?: SheetGeometryCategoryTable<SheetGeometrySelectorEffectMap["selector"]>;
}): SheetGeometrySourceFilter {
  const filterCategories = Array.from(
    new Set([
      ...OverridableSheetGeometryCategories,
      ...(Object.keys(provenanceSelectors) as SheetGeometryCategory[]),
      ...(Object.keys(reportingUnitSelectors) as SheetGeometryCategory[]),
      ...(Object.keys(builtInSelectors) as SheetGeometryCategory[]),
      ...(Object.keys(builtInFilters) as SheetGeometryCategory[]),
    ]),
  );

  const filters = filterCategories.reduce((categoryMap, category) => {
    const geometryPredicate = (g: TaggedPlanGeometry) => {
      const builtInSelector = builtInSelectors[category];
      if (builtInSelector && !builtInSelector(g)) {
        return false;
      }

      const provenanceSelector = provenanceSelectors[category];
      if (provenanceSelector && !provenanceSelector(g)) {
        return false;
      }

      const reportingUnitSelector = reportingUnitSelectors[category];
      if (reportingUnitSelector && !evalSheetGeometrySelector(g, reportingUnitSelector)) {
        return false;
      }

      return evaluateVisibilityEffects(g, selectorEffectMap);
    };

    const builtInFilter = builtInFilters[category];
    if (!builtInFilter) {
      categoryMap[category] = async (source, tag) => {
        const geometries = await source.getPlanGeometry(tag);
        return geometries.filter(geometryPredicate);
      };
      return categoryMap;
    }

    categoryMap[category] = async (source, tag) => {
      const geometries = await builtInFilter(source, tag);
      return geometries.filter(geometryPredicate);
    };
    return categoryMap;
  }, {} as SheetGeometryCategoryTable<SheetGeometryFilter>);

  const groupFilters = OverridableSheetGeometryGroupCategories.reduce((categoryMap, category) => {
    categoryMap[category] = filterForGeometryGroupCondition((g) => {
      const builtInSelector = builtInSelectors[category];
      if (builtInSelector && !builtInSelector(g.geometries[0])) {
        return false;
      }
      return true;
    });
    return categoryMap;
  }, {});

  return new SheetGeometrySourceFilter(filters, groupFilters);
}

/**
 * Generates a styling function that applies style and scale effects to the geometry.
 */
export function makeSheetGeometryStyler({
  selectorEffectMap = [],
  viewSpecificStyles = {},
  defaultStyles = planDefaultCategoryStyles,
  effects = {},
}: {
  readonly selectorEffectMap?: SheetGeometrySelectorEffectMap[];
  readonly viewSpecificStyles?: SheetGeometryCategoryStyleSheets;
  readonly defaultStyles?: SheetGeometryCategoryStyleSheets;
  readonly effects?: Table<Effect>;
}): SheetGeometryStyler {
  const effectsStyleSheet = makeStyleEffectsStyleSheets({ effects });

  return (geometry, category) => {
    const defaultStyleSheet: SheetGeometryCategoryStyle = defaultStyles[category] ?? {};
    const viewSpecificStyleSheet: SheetGeometryCategoryStyle = viewSpecificStyles[category] ?? {};

    const categoryStyleSheet = styleSheetCascade<SheetGeometryCategoryStyle>(
      defaultStyleSheet,
      viewSpecificStyleSheet,
      effectsStyleSheet,
    );

    return geometry.map((g) => {
      g = evaluateStyleAndScaleEffects(g, selectorEffectMap);
      g = resolveSheetGeometryStyle(g, categoryStyleSheet);
      g = resolveSheetGeometryScale(g, effects);
      return g;
    });
  };
}

export function relaxationInteractionMapFromEffects(
  selectorEffectMap: SheetGeometrySelectorEffectMap[],
): SheetGeometryCategoryTable<RelaxationObstacle[]> {
  const interactionMap: SheetGeometryCategoryTable<RelaxationObstacle[]> = {};

  for (const { selector, effects } of selectorEffectMap) {
    for (const effect of effects) {
      if (!isRelaxationObstacleEffect(effect)) {
        continue;
      }

      const targetCategory = effect.targetCategory;
      if (!targetCategory) {
        continue;
      }
      if (!interactionMap[targetCategory]) {
        interactionMap[targetCategory] = [];
      }
      for (const category of effect.categories) {
        interactionMap[targetCategory].push({
          category,
          selector,
        });
      }
    }
  }

  return interactionMap;
}

export function hasRelaxationEffects(viewTemplate: ViewTemplate, effects: Table<Effect>): boolean {
  for (const id in effects) {
    const effect = effects[id];
    if (isRelaxationObstacleEffect(effect)) {
      if (
        effect.parentId !== undefined &&
        viewTemplate.viewTemplateOverrideIds.includes(docEntityIdFromString("VIEW_TEMPLATE_OVERRIDE", effect.parentId))
      ) {
        return true;
      }
    }
  }
  return false;
}

const sheetGeometryCategoryWithLazyObstacleInitialization = new Set([SheetGeometryCategory.ROOM_LABELS]);

export function hasLazyRelaxationObstacleObbs(sheetGeometryCategory: SheetGeometryCategory) {
  return sheetGeometryCategoryWithLazyObstacleInitialization.has(sheetGeometryCategory);
}
